import { z } from 'zod';
import { GYM_STATUSES, INTEGRATION_KINDS, INTEGRATION_STATUSES } from '@bulk/types';
import { dateOnlySchema, idSchema, pageSchema, paginationQuerySchema } from './common';
import { setupProgressSchema } from './setup-guide';

export const platformGymSummarySchema = z.object({
  id: idSchema,
  name: z.string(),
  slug: z.string(),
  status: z.enum(GYM_STATUSES),
  country: z.string(),
  currency: z.string(),
  timezone: z.string(),
  memberCount: z.number().int(),
  activeMembershipCount: z.number().int(),
  primaryHostname: z.string().nullable(),
  integrations: z.array(
    z.object({
      kind: z.enum(INTEGRATION_KINDS),
      provider: z.string(),
      status: z.enum(INTEGRATION_STATUSES),
    }),
  ),
  onboardingComplete: z.boolean(),
  setup: setupProgressSchema.describe('How many of the required steps of the setup guide are finished'),
  createdAt: z.string(),
  activatedAt: z.string().nullable(),
});
export type PlatformGymSummaryDto = z.infer<typeof platformGymSummarySchema>;

export const ONBOARDING_STEPS = [
  'basics',
  'branding',
  'domain',
  'email',
  'payment',
  'access',
  'plans',
  'trainers',
  'classes',
  'website',
  'verification',
] as const;
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

export const onboardingStateSchema = z.object({
  steps: z.array(
    z.object({
      key: z.enum(ONBOARDING_STEPS),
      complete: z.boolean(),
      optional: z.boolean(),
      hint: z.string(),
    }),
  ),
  canActivate: z.boolean(),
  blockers: z.array(z.string()),
});
export type OnboardingStateDto = z.infer<typeof onboardingStateSchema>;

export const dashboardStatsSchema = z.object({
  activeMembers: z.number().int(),
  newMembers: z.number().int(),
  expiringMemberships: z.number().int(),
  revenueMinor: z.number().int(),
  currency: z.string(),
  upcomingSessions: z.number().int(),
  todaysBookings: z.number().int(),
  todaysAttendance: z.number().int(),
  accessEventsToday: z.number().int(),
  accessDeniedToday: z.number().int(),
  /** The branches the money, sessions and taps are counted at, for someone who works only at some; null for the whole gym. */
  branches: z.array(z.string()).nullable(),
  recentPayments: z.array(z.record(z.string(), z.unknown())),
  integrations: z.array(
    z.object({
      kind: z.enum(INTEGRATION_KINDS),
      provider: z.string(),
      status: z.enum(INTEGRATION_STATUSES),
    }),
  ),
  from: z.string(),
  to: z.string(),
});
export type DashboardStatsDto = z.infer<typeof dashboardStatsSchema>;

export const dashboardQuerySchema = z.object({
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional(),
});

export const failedJobSchema = z.object({
  id: idSchema,
  name: z.string(),
  gymId: idSchema.nullable(),
  gymName: z.string().nullable(),
  attempts: z.number().int(),
  maxAttempts: z.number().int(),
  lastError: z.string().nullable(),
  payload: z.record(z.string(), z.unknown()),
  createdAt: z.string(),
  lastTriedAt: z.string().describe('When the job last ran and failed for good'),
});
export type FailedJobDto = z.infer<typeof failedJobSchema>;

export const failedJobListSchema = pageSchema(failedJobSchema).extend({
  names: z.array(z.string()).describe('Every job type that has failed jobs, for the filter'),
});
export type FailedJobListDto = z.infer<typeof failedJobListSchema>;

export const failedJobQuerySchema = paginationQuerySchema.extend({
  name: z.string().trim().max(120).optional(),
});
export type FailedJobQuery = z.infer<typeof failedJobQuerySchema>;

export const factsRebuildSchema = z.object({
  from: dateOnlySchema.optional().describe('First day to compute again; two years back when left out'),
  to: dateOnlySchema.optional().describe('Last day to compute again; the last settled day when left out'),
});
export type FactsRebuildInput = z.infer<typeof factsRebuildSchema>;

export const factsRebuildResultSchema = z.object({
  jobId: idSchema,
  from: dateOnlySchema,
  to: dateOnlySchema,
});
export type FactsRebuildResultDto = z.infer<typeof factsRebuildResultSchema>;
